import React, { useState, useEffect } from 'react';

const FooterAdmin = () => {
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchFooterData();
  }, []);

  const fetchFooterData = async () => {
    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/footer-section');
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch data');
      }
      
      setFooterData(result.data);
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error fetching footer data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (field, value) => {
    setFooterData({
      ...footerData,
      logo: {
        ...footerData.logo,
        [field]: value
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFooterData({
      ...footerData,
      [name]: value
    });
  };

  const handleSectionChange = (sectionIndex, field, value) => {
    const updatedSections = [...footerData.sections];
    updatedSections[sectionIndex] = {
      ...updatedSections[sectionIndex],
      [field]: value
    };
    setFooterData({
      ...footerData,
      sections: updatedSections
    });
  };

  const handleSectionLinkChange = (sectionIndex, linkIndex, field, value) => {
    const updatedSections = [...footerData.sections];
    updatedSections[sectionIndex].links[linkIndex] = {
      ...updatedSections[sectionIndex].links[linkIndex],
      [field]: value
    };
    setFooterData({
      ...footerData,
      sections: updatedSections
    });
  };

  const addSection = () => {
    const newSection = {
      id: Date.now().toString(),
      title: "",
      links: []
    };
    setFooterData({
      ...footerData,
      sections: [...footerData.sections, newSection]
    });
  };

  const removeSection = (index) => {
    if (footerData.sections.length > 1) {
      const updatedSections = [...footerData.sections];
      updatedSections.splice(index, 1);
      setFooterData({
        ...footerData,
        sections: updatedSections
      });
    }
  };

  const addSectionLink = (sectionIndex) => {
    const newLink = {
      id: Date.now().toString(),
      text: "",
      url: "#"
    };
    const updatedSections = [...footerData.sections];
    updatedSections[sectionIndex].links.push(newLink);
    setFooterData({
      ...footerData,
      sections: updatedSections
    });
  };

  const removeSectionLink = (sectionIndex, linkIndex) => {
    if (footerData.sections[sectionIndex].links.length > 1) {
      const updatedSections = [...footerData.sections];
      updatedSections[sectionIndex].links.splice(linkIndex, 1);
      setFooterData({
        ...footerData,
        sections: updatedSections
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/footer-section', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(footerData),
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to update data');
      }
      
      setFooterData(result.data);
      setMessage({ text: 'Footer updated successfully!', type: 'success' });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error updating footer:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/footer-section/toggle', {
        method: 'PATCH',
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to toggle status');
      }
      
      setFooterData(result.data);
      setMessage({ 
        text: `Footer ${result.data.isActive ? 'activated' : 'deactivated'} successfully!`, 
        type: 'success' 
      });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error toggling footer status:', err);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default data? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/footer-section/reset', {
        method: 'POST',
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to reset data');
      }
      
      setFooterData(result.data);
      setMessage({ text: 'Footer reset to default successfully!', type: 'success' });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error resetting footer:', err);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!footerData) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          Failed to load footer data. Please try again.
        </div>
        <button className="btn btn-primary" onClick={fetchFooterData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Manage Footer</h2>
      
      {message.text && (
        <div className={`alert alert-${message.type}`} role="alert">
          {message.text}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title">Footer Status</h5>
            <button 
              className={`btn ${footerData.isActive ? 'btn-warning' : 'btn-success'}`}
              onClick={handleToggleActive}
            >
              {footerData.isActive ? 'Deactivate Footer' : 'Activate Footer'}
            </button>
          </div>
          <p className="card-text">
            Current status: <span className={`badge bg-${footerData.isActive ? 'success' : 'danger'}`}>
              {footerData.isActive ? 'Active' : 'Inactive'}
            </span>
          </p>
          <button className="btn btn-outline-danger" onClick={handleReset}>
            Reset to Default
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Logo & Description</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label">Logo URL</label>
              <input
                type="text"
                className="form-control"
                value={footerData.logo?.url || ''}
                onChange={(e) => handleLogoChange('url', e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Logo Alt Text</label>
              <input
                type="text"
                className="form-control"
                value={footerData.logo?.alt || ''}
                onChange={(e) => handleLogoChange('alt', e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Logo Link</label>
              <input
                type="text"
                className="form-control"
                value={footerData.logo?.link || ''}
                onChange={(e) => handleLogoChange('link', e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={footerData.description || ''}
                onChange={handleInputChange}
                name="description"
              ></textarea>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Footer Sections</h5>
            <button type="button" className="btn btn-sm btn-primary" onClick={addSection}>
              Add Section
            </button>
          </div>
          <div className="card-body">
            {footerData.sections && footerData.sections.map((section, sectionIndex) => (
              <div key={section.id} className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Section #{sectionIndex + 1}</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeSection(sectionIndex)}
                    disabled={footerData.sections.length <= 1}
                  >
                    Remove Section
                  </button>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Section Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={section.title || ''}
                      onChange={(e) => handleSectionChange(sectionIndex, 'title', e.target.value)}
                      placeholder="Section title"
                    />
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6>Links</h6>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => addSectionLink(sectionIndex)}
                    >
                      Add Link
                    </button>
                  </div>
                  
                  {section.links && section.links.map((link, linkIndex) => (
                    <div key={link.id} className="row mb-3">
                      <div className="col-md-5">
                        <label className="form-label">Link Text</label>
                        <input
                          type="text"
                          className="form-control"
                          value={link.text || ''}
                          onChange={(e) => handleSectionLinkChange(sectionIndex, linkIndex, 'text', e.target.value)}
                          placeholder="Link text"
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label">Link URL</label>
                        <input
                          type="text"
                          className="form-control"
                          value={link.url || ''}
                          onChange={(e) => handleSectionLinkChange(sectionIndex, linkIndex, 'url', e.target.value)}
                          placeholder="Link URL"
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeSectionLink(sectionIndex, linkIndex)}
                          disabled={section.links.length <= 1}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Copyright</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label">Copyright Text</label>
              <input
                type="text"
                className="form-control"
                value={footerData.copyright?.text || ''}
                onChange={(e) => setFooterData({
                  ...footerData,
                  copyright: {
                    ...footerData.copyright,
                    text: e.target.value
                  }
                })}
              />
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between">
          <button type="button" className="btn btn-secondary" onClick={fetchFooterData}>
            Reset Changes
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FooterAdmin;